import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Input } from '@heroui/input';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';

const ResetPassword: React.FC = () => {
  const pageTitle = 'Reset Password';
  const pageDescription = 'Enter your new password to complete the reset.';
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');
    if (tokenParam) setToken(tokenParam);
    if (emailParam) setEmail(emailParam);
  }, [searchParams]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = 'Invalid email format';
    if (!token) newErrors.token = 'Invalid or expired reset link';
    if (!newPassword) newErrors.newPassword = 'Password is required';
    else if (newPassword.length < 6)
      newErrors.newPassword = 'Password must be at least 6 characters';
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || success) return;
    setIsLoading(true);
    try {
      await resetPassword({ email, token, newPassword });
      setSuccess(true);
      toast.success('Password reset successfully.');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          'Invalid or expired reset link. Please request a new one.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
          <div className='max-w-md w-full'>
            <Card>
              <CardBody className='text-center py-10'>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  Invalid reset link
                </h2>
                <p className='text-gray-600 mb-6'>
                  This password reset link is invalid or has expired. Please
                  request a new one.
                </p>
                <Button as={Link} to='/forgot-password' color='primary'>
                  Request new reset link
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <SEOHead title={pageTitle} description={pageDescription} />
        <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
          <div className='max-w-md w-full'>
            <Card>
              <CardBody className='text-center py-10'>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  Password reset successfully
                </h2>
                <p className='text-gray-600 mb-6'>
                  You can now sign in with your new password.
                </p>
                <Button as={Link} to='/login' color='primary'>
                  Sign in
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <h1 className='text-3xl font-bold text-gray-900'>
              Set new password
            </h1>
            <p className='mt-2 text-sm text-gray-600'>
              Enter your email and a new password below.
            </p>
          </div>

          <Card>
            <CardHeader className='pb-2'>
              <h2 className='text-xl font-semibold text-center w-full'>
                Reset Password
              </h2>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSubmit} className='space-y-6'>
                <Input
                  type='email'
                  label='Email address'
                  placeholder='Enter your email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  variant='bordered'
                  size='lg'
                  isInvalid={!!errors.email}
                  errorMessage={errors.email}
                  required
                />
                <Input
                  type='password'
                  label='New password'
                  placeholder='Enter new password'
                  value={newPassword}
                  onChange={e => {
                    setNewPassword(e.target.value);
                    setErrors(prev => ({ ...prev, newPassword: '' }));
                  }}
                  variant='bordered'
                  size='lg'
                  isInvalid={!!errors.newPassword}
                  errorMessage={errors.newPassword}
                  minLength={6}
                  required
                />
                <Input
                  type='password'
                  label='Confirm new password'
                  placeholder='Confirm new password'
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                    setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  variant='bordered'
                  size='lg'
                  isInvalid={!!errors.confirmPassword}
                  errorMessage={errors.confirmPassword}
                  required
                />
                <Button
                  type='submit'
                  color='primary'
                  size='lg'
                  className='w-full'
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>

              <div className='mt-4 text-center'>
                <Link
                  to='/login'
                  className='text-sm text-primary hover:text-primary-500'
                >
                  Back to sign in
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
